import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Grid, Button } from '@mui/material';
import ContractIcon from '@mui/icons-material/ReceiptLong';
import { FilterList } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import SearchBarByType from 'src/components/SearchBarByType';
import Filter from 'src/components/Filter/Filter';
import useDebounce from 'src/hooks/useDebounce';
import {
  loadBureauReportDetail,
  SAVE_BUREAU_REPORT_DETAIL_OPTIONS
} from 'src/store/actions/bureauReportActions';
import { ContractPaymentStatus } from 'src/utils/constants/contracts';
import { ReportType } from 'src/utils/constants/buro-reports';
import useStyles from './styles';

const searchTypes = [{ value: 'clientName', label: 'CLIENT', type: 'text' }];

const filters = [
  {
    title: 'BUREAU_REPORTS.PAYMENT_STATUS',
    controlGroup: 'checkbox',
    items: [
      {
        label: 'BUREAU_REPORTS.PAYMENT_STATUS_ON_TIME',
        value: ContractPaymentStatus.UP_TO_DATE
      },
      {
        label: 'BUREAU_REPORTS.PAYMENT_STATUS_OVERDUE',
        value: ContractPaymentStatus.OVERDUE
      }
    ]
  }
];

function getFilterOptions(options) {
  let paymentStatus = null;

  if (
    options[ContractPaymentStatus.OVERDUE] &&
    !options[ContractPaymentStatus.UP_TO_DATE]
  ) {
    paymentStatus = ContractPaymentStatus.OVERDUE;
  } else if (
    options[ContractPaymentStatus.UP_TO_DATE] &&
    !options[ContractPaymentStatus.OVERDUE]
  ) {
    paymentStatus = ContractPaymentStatus.UP_TO_DATE;
  }

  return { paymentStatus };
}

const Toolbar = ({ totalContracts, reportType }) => {
  const isPartial = reportType === ReportType.PARTIAL;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { reportId } = useParams();

  const [anchorEl, setAnchorEl] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [searchCriteria, setSearchCriteria] = useState('clientName');

  const debounceSearch = useDebounce((search, filterOpts) => {
    if (!reportId) return;

    const searchParams = { ...search, ...filterOpts };
    dispatch({
      type: SAVE_BUREAU_REPORT_DETAIL_OPTIONS,
      searchParams
    });
    dispatch(
      loadBureauReportDetail({
        reportId: Number(reportId),
        page: 0,
        limit: +process.env.REACT_APP_TABLE_SIZE,
        ...searchParams
      })
    );
  }, 400);

  const handleClickFilter = useCallback(
    event => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleCloseFilter = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleFilterChange = useCallback(
    event => {
      setFilterValues(event);
      const filterOptions = getFilterOptions(event);
      debounceSearch({ [searchCriteria]: searchValue }, filterOptions);
    },
    [setFilterValues, searchValue, searchCriteria, debounceSearch, reportId]
  );

  const handleSearch = useCallback(
    (e, criteria) => {
      const { value } = e.target;
      setSearchValue(value);
      setSearchCriteria(criteria);
      const filterOptions = getFilterOptions(filterValues);
      debounceSearch({ [criteria]: value }, filterOptions);
    },
    [setSearchValue, setSearchCriteria, debounceSearch, filterValues, reportId]
  );

  const handleSearchCriteria = useCallback(() => {
    if (!reportId) return;

    setSearchValue('');
    dispatch({
      type: SAVE_BUREAU_REPORT_DETAIL_OPTIONS,
      searchParams: {}
    });
    dispatch(
      loadBureauReportDetail({
        reportId: Number(reportId),
        page: 0,
        limit: +process.env.REACT_APP_TABLE_SIZE
      })
    );
  }, [dispatch, reportId]);

  return (
    <Box mt={3}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              margin: '0px 20px'
            }}
          >
            <Typography
              style={{ fontWeight: '400', textTransform: 'capitalize' }}
              color="primary"
              variant="h1"
            >
              {t('BUREAU_REPORTS.DETAIL_TITLE')}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <SearchBarByType
                types={searchTypes}
                handleSearch={handleSearch}
                searchValue={searchValue}
                handleSearchCriteria={handleSearchCriteria}
              />
              {!isPartial && (
                <>
                  <Filter
                    anchorEl={anchorEl}
                    filterValues={filterValues}
                    handleClose={handleCloseFilter}
                    handleChange={handleFilterChange}
                    filters={filters}
                  />
                  <Button
                    className={classes.filterButton}
                    color="primary"
                    onClick={handleClickFilter}
                  >
                    <FilterList className={classes.icon} />
                    {t('COMMON.FILTER')}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={7} lg={12}>
          <Grid container className={classes.transactionContainer}>
            <Grid item lg={4} className={classes.transaction}>
              <Box className={classes.transactionIcon}>
                <ContractIcon fill="#28A745" />
              </Box>
              <Grid item>
                <Typography
                  style={{ marginBottom: 8 }}
                  color="textPrimary"
                  component="p"
                  variant="body2"
                >
                  {t('COMMON.CONTRACTS')}
                </Typography>
                <Typography color="primary" component="h2" variant="h2">
                  {totalContracts || t('COMMON.EMPTY')}
                </Typography>
              </Grid>
            </Grid>
            <Box />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

Toolbar.propTypes = {
  totalContracts: PropTypes.number,
  reportType: PropTypes.string
};

export default Toolbar;
